import React, { FC, useCallback, useEffect, useMemo } from 'react';
import {
  faDownload,
  faEye,
  faTrash,
  Icon,
  usePreviewModal,
  CustomColumns,
  CustomTable,
} from '@/ui/components';
import { StyledLink, StyledLinkContainer } from '@/ui/globalStyles';
import { timeConverter } from '@/utils/helpers';
import { useSearchParams } from 'react-router-dom';
import { useAppCustomers } from '@/hooks';
import { useDeleteDocument, useDocuments } from '@/api/DocumentManagement';
import { Document } from '@/types';
import { App } from 'antd';
import { useTranslate } from '@/translations';
import { RelatedDocumentsDataGrid } from '../RelatedDocumentsDataGrid/RelatedDocumentsDataGrid';

export const DocumentManagementDataGrid: FC = () => {
  const { t } = useTranslate();
  const { modal } = App.useApp();
  const { customerIds, hasAdminAccess, hasGbaAccess } = useAppCustomers();
  const { data: documents, isFetching, refetch } = useDocuments();
  const { mutate: deleteDocumentMutate } = useDeleteDocument();
  const [searchParams] = useSearchParams();
  const { openPreviewModal, RenderPreviewModal } = usePreviewModal();

  useEffect(() => {
    refetch();
  }, [customerIds, refetch]);

  // doing it because of searching purposes, FilterRow using "contains" only for strings
  const dataSource = useMemo(
    () =>
      documents
        ?.filter(
          hasGbaAccess
            ? () => true
            : (document) => !document.isCustomerPreviewRestricted,
        )
        .map((document) => ({
          ...document,
          relatedDocumentsString: document.relatedDocuments.join(', '),
        })) || [],
    [documents, hasGbaAccess],
  );

  const searchId = useMemo(() => searchParams.get('id'), [searchParams]);

  const onDeleteDocument = useCallback(
    (documentId: number) => {
      modal.confirm({
        title: 'Confirm',
        content: t('deleteRecordQuestion'),
        okText: t('yes'),
        onOk: () => deleteDocumentMutate(documentId),
        onCancel: () => {},
      });
    },
    [deleteDocumentMutate, modal, t],
  );

  const columns: CustomColumns<Document> = useMemo(() => {
    const base: CustomColumns<Document> = [
      {
        dataIndex: 'documentType',
        title: 'Document Type',
        filter: true,
      },
      {
        dataIndex: 'entity',
        title: 'Entity',
        filter: true,
      },
      {
        dataIndex: 'entityReference',
        title: 'Entity Reference',
        filter: true,
        filteredValue: searchId ? [searchId] : undefined,
      },
      {
        dataIndex: 'origin',
        title: 'Origin',
        filter: true,
      },
      {
        dataIndex: 'destination',
        title: 'Destination',
        filter: true,
      },
      {
        dataIndex: 'createdDate',
        title: 'Created Date',
        renderType: 'date',
        minWidth: 200,
        width: 200,
        render: (value) => timeConverter(value),
        filter: true,
      },
      {
        dataIndex: 'documentId',
        title: 'Actions',
        minWidth: 100,
        width: 100,
        align: 'center',
        render: (_, { documentId, fileUrl, fileName }) => (
          <StyledLinkContainer>
            <StyledLink
              onClick={() => openPreviewModal({ src: fileUrl, fileName })}
              aria-label="View"
            >
              <Icon icon={faEye} />
            </StyledLink>
            <a href={fileUrl} download={fileName} aria-label="Download">
              <Icon icon={faDownload} />
            </a>
            {hasAdminAccess ? (
              <StyledLink
                onClick={() => onDeleteDocument(documentId)}
                aria-label="Delete"
              >
                <Icon icon={faTrash} />
              </StyledLink>
            ) : null}
          </StyledLinkContainer>
        ),
      },
    ];

    if (customerIds.length > 0) {
      return [
        {
          dataIndex: 'customerName',
          title: 'Customer',
          filter: true,
        },
        ...base,
      ];
    }

    return base;
  }, [
    customerIds.length,
    hasAdminAccess,
    onDeleteDocument,
    openPreviewModal,
    searchId,
  ]);

  const masterDetailRender = useCallback(
    (data: Document) => (
      <RelatedDocumentsDataGrid data={data} documents={documents || []} />
    ),
    [documents],
  );

  return (
    <>
      <CustomTable
        dataSource={dataSource}
        rowKey="documentId"
        loading={isFetching}
        columns={columns}
        expandable={{
          expandedRowRender: (data) => masterDetailRender(data),
        }}
      />

      <RenderPreviewModal />
    </>
  );
};
